import React, { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import ImportProgressDialog from "@/components/ImportProgressDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash, PencilSimple, DownloadSimple, UploadSimple, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";

const emptyForm = {
  name: "", type: "customer", email: "", phone: "", hp_wa: "",
  is_pkp: false, npwp: "", npwp16: "", nik: "", nitku: "",
  address: "", city: "", province: "", postal_code: "",
  payment_terms_days: 30, opening_ar: 0, opening_ap: 0,
  tax_regulation_ids: [], notes: "",
};

export default function Partners() {
  const [tab, setTab] = useState("customer");
  const [importProg, setImportProg] = useState(null); // { loading, fileName, result }
  const [selected, setSelected] = useState([]);
  const [bulkDlg, setBulkDlg] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get(`/partners?type=${tab}`).then((r) => setPartners(r.data));
  const [taxRegs, setTaxRegs] = useState([]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { api.get("/tax-regulations").then(r => setTaxRegs(r.data)).catch(() => {}); }, []);
  const filteredPartners = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return partners;
    return partners.filter(p => [p.name, p.partner_code, p.email, p.phone, p.hp_wa, p.npwp, p.npwp16, p.address, p.city, p.notes]
      .some(v => (v || "").toString().toLowerCase().includes(q)));
  })();
  const pg = usePaginated(filteredPartners);

  const save = async () => {
    try {
      const payload = { ...form, payment_terms_days: Number(form.payment_terms_days),
                        opening_ar: Number(form.opening_ar), opening_ap: Number(form.opening_ap) };
      if (editing) await api.put(`/partners/${editing.id}`, payload);
      else await api.post("/partners", payload);
      toast.success("Tersimpan");
      setForm(emptyForm); setEditing(null); setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const edit = (p) => { setEditing(p); setForm({ ...emptyForm, ...p }); setOpen(true); };
  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/partners/${id}`); load(); };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => {
    const ids = filteredPartners.map((p) => p.id);
    const allSel = ids.length > 0 && ids.every((id) => selected.includes(id));
    setSelected(allSel ? selected.filter((id) => !ids.includes(id)) : [...new Set([...selected, ...ids])]);
  };
  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const r = await api.post("/partners/bulk-delete", { ids: selected });
      toast.success(`${r.data.deleted} data berhasil dihapus`);
      setSelected([]); setBulkDlg(false); setBulkConfirm(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
    finally { setBulkBusy(false); }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("nusa_token");
    const resp = await fetch(`${API}/partners/import-template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "template_partners.xlsx"; a.click();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const token = localStorage.getItem("nusa_token");
    setImportProg({ loading: true, fileName: file.name });
    try {
      const resp = await fetch(`${API}/partners/import`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const j = await resp.json();
      if (!resp.ok) { setImportProg(null); throw new Error(j.detail || "Gagal"); }
      setImportProg({ loading: false, fileName: file.name, result: j });
      load();
    } catch (er) { setImportProg(null); toast.error(er.message); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="partners-page">
      <div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900">Pelanggan & Vendor</h1>
        <p className="text-sm text-slate-500 mt-1">Kelola data pelanggan dan pemasok — lengkap dengan status PKP, NPWP 16-digit & NITKU untuk e-Faktur.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <TabsList>
            <TabsTrigger value="customer" data-testid="tab-customer">Pelanggan</TabsTrigger>
            <TabsTrigger value="vendor" data-testid="tab-vendor">Vendor / Pemasok</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"><DownloadSimple size={14} className="mr-1" />Template</Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={uploadFile} data-testid="import-file" />
            {selected.length > 0 && <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => { setBulkConfirm(""); setBulkDlg(true); }} data-testid="bulk-delete-btn"><Trash size={14} className="mr-1" />Hapus Terpilih ({selected.length})</Button>}
            <ImportProgressDialog open={!!importProg} loading={importProg?.loading || false} fileName={importProg?.fileName} result={importProg?.result} onClose={() => setImportProg(null)} />
            <Dialog open={bulkDlg} onOpenChange={(o) => { if (!o && !bulkBusy) { setBulkDlg(false); setBulkConfirm(""); } }}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="flex items-center gap-2" data-testid="bulk-delete-title"><Trash size={18} className="text-rose-600" />Hapus {selected.length} Data Terpilih?</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="text-sm text-slate-600">Anda akan menghapus <b>{selected.length} {tab === "customer" ? "pelanggan" : "vendor"}</b> secara permanen. Tindakan ini <b>tidak dapat dibatalkan</b>.</div>
                  <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2 max-h-28 overflow-y-auto">
                    {selected.slice(0, 5).map((id) => filteredPartners.find((p) => p.id === id)?.name).filter(Boolean).map((n, i) => <div key={i}>• {n}</div>)}
                    {selected.length > 5 && <div className="text-slate-500">…dan {selected.length - 5} lainnya</div>}
                  </div>
                  <div className="bg-rose-50 border border-rose-200 rounded p-3 space-y-2">
                    <Label className="text-rose-800 text-xs">Ketik <b>HAPUS</b> untuk konfirmasi</Label>
                    <Input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)} placeholder="HAPUS" data-testid="bulk-delete-confirm-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => { setBulkDlg(false); setBulkConfirm(""); }} data-testid="bulk-delete-cancel">Batal</Button>
                    <Button className="flex-1 bg-rose-600 hover:bg-rose-700" disabled={bulkConfirm !== "HAPUS" || bulkBusy} onClick={bulkDelete} data-testid="bulk-delete-submit">{bulkBusy ? "Menghapus…" : `Hapus ${selected.length} Data`}</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="import-btn"><UploadSimple size={14} className="mr-1" />Impor Excel</Button>
            <Button variant="outline" onClick={async () => { try { const token = localStorage.getItem("nusa_token"); const resp = await fetch(`${API}/export/partners`, { headers: { Authorization: `Bearer ${token}` } }); if (!resp.ok) throw new Error("Gagal export"); const blob = await resp.blob(); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `partners_${new Date().toISOString().slice(0,10)}.xlsx`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 5000); toast.success("Data pelanggan & vendor diekspor"); } catch (e) { toast.error(e.message); } }} data-testid="export-partners-btn" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"><DownloadSimple size={14} className="mr-1" />Export Excel</Button>
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm({ ...emptyForm, type: tab }); } }}>
              <DialogTrigger asChild><Button className="bg-amber-600 hover:bg-amber-700" data-testid="new-partner-btn" onClick={() => setForm({ ...emptyForm, type: tab })}><Plus size={16} className="mr-1" /> Tambah</Button></DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0">
                <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>{editing ? "Edit" : "Tambah"} {tab === "customer" ? "Pelanggan" : "Vendor"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
                  <div className="col-span-2"><Label>Nama Perusahaan / Perorangan</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="partner-name-input" /></div>
                  <div><Label>Tipe</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger data-testid="partner-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="customer">Pelanggan</SelectItem>
                        <SelectItem value="vendor">Vendor</SelectItem>
                        <SelectItem value="both">Keduanya</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Status PKP</Label>
                    <Select value={form.is_pkp ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, is_pkp: v === "yes" })}>
                      <SelectTrigger data-testid="partner-pkp-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">PKP (Pengusaha Kena Pajak)</SelectItem>
                        <SelectItem value="no">Non-PKP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>NPWP 16-digit</Label><Input value={form.npwp16} onChange={(e) => setForm({ ...form, npwp16: e.target.value })} maxLength={16} placeholder="1234567890123456" data-testid="npwp16-input" /></div>
                  <div><Label>NIK (bila tanpa NPWP)</Label><Input value={form.nik} onChange={(e) => setForm({ ...form, nik: e.target.value })} maxLength={16} placeholder="16 digit — dipakai untuk e-Faktur" data-testid="nik-input" /></div>
                  <div><Label>NITKU / IDTKU</Label><Input value={form.nitku} onChange={(e) => setForm({ ...form, nitku: e.target.value })} placeholder="1234567890123456000000" data-testid="nitku-input" /></div>
                  <div className="col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="email-input" /></div>
                  <div><Label>No HP / WhatsApp</Label><Input value={form.hp_wa} onChange={(e) => setForm({ ...form, hp_wa: e.target.value })} placeholder="081234567890" data-testid="hpwa-input" /></div>
                  <div><Label>Telepon Kantor</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Alamat Lengkap</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
                  <div><Label>Kota</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>Provinsi</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
                  <div><Label>Kode Pos</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
                  <div><Label>Termin Pembayaran</Label>
                    <Select value={String(form.payment_terms_days ?? 30)} onValueChange={(v) => setForm({ ...form, payment_terms_days: Number(v) })}>
                      <SelectTrigger data-testid="partner-terms-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">COD (bayar di tempat)</SelectItem>
                        <SelectItem value="7">7 hari</SelectItem>
                        <SelectItem value="14">14 hari</SelectItem>
                        <SelectItem value="30">30 hari</SelectItem>
                        <SelectItem value="45">45 hari</SelectItem>
                        <SelectItem value="60">60 hari</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Saldo Awal Piutang</Label><MoneyInput value={form.opening_ar} onChange={(e) => setForm({ ...form, opening_ar: e.target.value })} data-testid="partner-opening-ar" /></div>
                  <div><Label>Saldo Awal Hutang</Label><MoneyInput value={form.opening_ap} onChange={(e) => setForm({ ...form, opening_ap: e.target.value })} data-testid="partner-opening-ap" /></div>
                  {taxRegs.length > 0 && (
                    <div className="col-span-2">
                      <Label>Regulasi Pajak <span className="text-[11px] text-slate-500 font-normal">(auto-terapkan saat faktur dibuat untuk partner ini)</span></Label>
                      <div className="mt-1 space-y-1.5 bg-slate-50 border border-slate-200 rounded p-2" data-testid="partner-tax-regs">
                        {taxRegs.map(r => {
                          const checked = (form.tax_regulation_ids || []).includes(r.id);
                          return (
                            <label key={r.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`tax-reg-check-${r.id}`}>
                              <input type="checkbox" checked={checked} onChange={(e) => {
                                const cur = new Set(form.tax_regulation_ids || []);
                                if (e.target.checked) cur.add(r.id); else cur.delete(r.id);
                                setForm({ ...form, tax_regulation_ids: Array.from(cur) });
                              }} />
                              <span className="font-medium">{r.name}</span>
                              <span className="text-slate-500 tabular-nums">({r.rate}%)</span>
                              {r.description && <span className="text-slate-400 truncate">— {r.description}</span>}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="col-span-2"><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                  <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3 sm:rounded-b-lg col-span-2">
                    <Button onClick={save} className="w-full bg-slate-900" data-testid="partner-save-btn">Simpan</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <TabsContent value={tab} className="mt-6">
          <div className="mb-3 relative w-full max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama, kode, email, NPWP, kota…" className="pl-8 h-9" data-testid="partners-search-input" />
          </div>
          <div className="bg-white border border-slate-200 rounded-md" data-testid="partners-table">
            <Table>
              <TableHeader><TableRow><TableHead className="w-8"><Checkbox checked={filteredPartners.length > 0 && filteredPartners.every((p) => selected.includes(p.id))} onCheckedChange={toggleAll} data-testid="select-all-checkbox" /></TableHead><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>PKP</TableHead><TableHead>NPWP 16</TableHead><TableHead>Email / HP</TableHead><TableHead>Kota</TableHead><TableHead>Termin</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredPartners.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-500 text-sm">{search ? "Tidak ada hasil yang cocok." : <>Belum ada data. Klik <b>Tambah</b> atau <b>Impor Excel</b> untuk mulai.</>}</TableCell></TableRow> : pg.paged.map((p) => (
                  <TableRow key={p.id} className={selected.includes(p.id) ? "bg-amber-50/50" : ""}>
                    <TableCell className="w-8"><Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggleSel(p.id)} data-testid={`sel-${p.id}`} /></TableCell>
                    <TableCell className="font-mono-plex text-xs">{p.partner_code}</TableCell>
                    <TableCell className="font-medium">{p.name}{p.address && <div className="text-xs text-slate-500 truncate max-w-[220px]">{p.address}</div>}</TableCell>
                    <TableCell>{p.is_pkp ? <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">PKP</span> : <span className="text-xs text-slate-400">—</span>}</TableCell>
                    <TableCell className="font-mono-plex text-xs">{p.npwp16 || p.npwp || "—"}</TableCell>
                    <TableCell><div className="text-sm">{p.email || "—"}</div><div className="text-xs text-slate-500">{p.hp_wa || p.phone || ""}</div></TableCell>
                    <TableCell className="text-sm">{p.city || "—"}</TableCell>
                    <TableCell>{p.payment_terms_days === 0 ? "COD" : `${p.payment_terms_days} hari`}</TableCell>
                    <TableCell className="flex gap-1">
                      <button onClick={() => edit(p)} className="text-slate-400 hover:text-slate-900" data-testid={`edit-${p.id}`}><PencilSimple size={14} /></button>
                      <button onClick={() => del(p.id)} className="text-slate-400 hover:text-rose-600" data-testid={`del-${p.id}`}><Trash size={14} /></button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {pg.pager}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
